import classNames from 'classnames';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { StateMessage } from '@/components/ui/controls';
import { Card, PageHeader } from '@/components/ui/page';
import { btn, field } from '@/components/ui/styles';
import http from '@/utils/http';
import { useApi } from '@/utils/use-api';

import type { MessageDetail, MessageInput, Template } from './api-types';
import MessagingNav from './messaging-nav';
import { ActionBar, AudienceList, ChannelPicker, NotFound, PreviewPanel, SegmentPicker, VariableBar, errorStatus, linkButtonLabel, linkOK, useVariableInsert } from './parts';

const TITLE_MAX = 120;
const EMPTY: MessageInput = { title: '', body: '', channels: ['email'], segment_ids: [], template_id: '' };

// The composer has no subject field: the title is the email subject, so none is kept.
const inputOf = (m: MessageDetail): MessageInput => ({
  title: m.title,
  body: m.body,
  channels: m.channels,
  segment_ids: m.segment_ids,
  template_id: m.template_id,
  link_url: m.link_url,
  link_label: m.link_label,
});

/** The composer for one message; keyed by id so switching between messages starts fresh. */
const Composer = ({ id, stored }: { id?: string; stored?: MessageDetail }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const templates = useApi<Template[]>('/templates');
  // "Duplicate" hands a message over as router state; an edit starts from the stored draft.
  const [draft, setDraft] = useState<MessageInput>(() => (location.state as MessageInput | null) ?? (stored ? inputOf(stored) : EMPTY));
  const [notice, setNotice] = useState<'saved' | 'failed' | 'gone' | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<MessageInput>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setNotice(null);
  };
  const { ref, insert } = useVariableInsert(draft.body, (body) => set({ body }));

  const popup = draft.channels.includes('popup');
  const badLink = popup && !linkOK((draft.link_url ?? '').trim());
  const missing = [
    !draft.title.trim() && 'a title',
    !draft.body.trim() && 'some text',
    !draft.channels.length && 'a channel',
    !draft.segment_ids.length && 'a segment',
    badLink && 'a link Owner Hub takes',
  ].filter(Boolean) as string[];

  // Saving a new draft moves to its edit page, so a second save rewrites it.
  const save = async (thenReview: boolean) => {
    setBusy(true);
    setNotice(null);
    // The link belongs to the Owner Hub notification: without that channel it is not kept (nor checked).
    const body = { ...draft, title: draft.title.trim(), subject: '', link_url: popup ? (draft.link_url ?? '').trim() : '', link_label: popup ? draft.link_label : '' };
    try {
      const { data } = id ? await http.put<MessageDetail>(`/messages/${id}`, body) : await http.post<MessageDetail>('/messages', body);
      // A new draft's composer is left out of the history, so Back never makes it again.
      if (thenReview) navigate(`/messaging/messages/${data.id}/review`, { replace: !id });
      else if (!id) navigate(`/messaging/messages/${data.id}/edit`, { replace: true, state: null });
      else setNotice('saved');
    } catch (err) {
      setNotice(errorStatus(err) === 409 ? 'gone' : 'failed');
    } finally {
      setBusy(false);
    }
  };

  const applyTemplate = (templateId: string) => {
    const t = templates.data?.find((x) => x.id === templateId);
    if (!t) return set({ template_id: '' });
    set({ template_id: t.id, title: (t.subject || t.name).slice(0, TITLE_MAX), body: t.body, channels: t.channels.length ? t.channels : draft.channels });
  };

  return (
    <div>
      <PageHeader
        title={id ? 'Edit message' : 'New message'}
        description="Write once, send on every channel you pick."
        back={stored ? { to: `/messaging/messages/${stored.id}`, label: stored.title } : { to: '/messaging/messages', label: 'Messages' }}
      >
        <MessagingNav />
      </PageHeader>

      <div className="grid grid-cols-1 gap-4 md:gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          {(templates.data?.length ?? 0) > 0 && (
            <div className="mb-4">
              <label className={field.label} htmlFor="compose-template">
                Start from a template
              </label>
              <select
                id="compose-template"
                className={classNames(field.select, 'w-full sm:w-auto')}
                value={draft.template_id ?? ''}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <option value="">No template</option>
                {templates.data?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className={field.label} htmlFor="compose-title">
            Title
          </label>
          <div className="relative">
            <input
              id="compose-title"
              className={classNames(field.input, 'pr-20')}
              maxLength={TITLE_MAX}
              placeholder="New feature announcement"
              value={draft.title}
              onChange={(e) => set({ title: e.target.value })}
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs tabular-nums text-fg-faint">
              {draft.title.length} / {TITLE_MAX}
            </span>
          </div>
          <p className="mt-1 text-xs text-fg-muted">The email subject, and the heading on Telegram and in the Owner Hub notification.</p>

          <label className={classNames(field.label, 'mt-4')} htmlFor="compose-body">
            Text
          </label>
          <textarea
            id="compose-body"
            ref={ref}
            rows={10}
            className={field.textarea}
            placeholder={'Hi {{first_name}},\n\n…'}
            value={draft.body}
            onChange={(e) => set({ body: e.target.value })}
          />
          <VariableBar onInsert={insert} />

          <p className={classNames(field.label, 'mt-5')}>Channels</p>
          <ChannelPicker value={draft.channels} onChange={(channels) => set({ channels })} />

          {draft.channels.includes('popup') && (
            <div className="mt-4 grid gap-3 sm:grid-cols-[2fr_1fr]">
              <div>
                <label className={field.label} htmlFor="compose-link">
                  Link in the Owner Hub notification <span className="text-fg-faint">(optional)</span>
                </label>
                <input
                  id="compose-link"
                  className={field.input}
                  maxLength={2000}
                  placeholder="/wallet or https://…"
                  aria-invalid={badLink}
                  aria-describedby="compose-link-help"
                  value={draft.link_url ?? ''}
                  onChange={(e) => set({ link_url: e.target.value })}
                />
              </div>
              <div>
                <label className={field.label} htmlFor="compose-link-label">
                  Button
                </label>
                <input
                  id="compose-link-label"
                  className={field.input}
                  maxLength={60}
                  placeholder="Open"
                  value={draft.link_label ?? ''}
                  onChange={(e) => set({ link_label: e.target.value })}
                />
              </div>
              <p id="compose-link-help" className={classNames('text-xs sm:col-span-2', badLink ? 'text-danger' : 'text-fg-muted')}>
                {badLink ? 'This link would not work: use an Owner Hub page, such as /wallet, or an https:// address.' : 'An Owner Hub page, such as /wallet, or an https address. The button shows under the text.'}
              </p>
            </div>
          )}

          <SegmentPicker className="mt-5" value={draft.segment_ids} onChange={(segment_ids) => set({ segment_ids })} />

          <ActionBar className="md:flex md:flex-wrap md:items-center md:justify-between md:gap-3">
            <p className="mb-2 text-xs text-fg-muted md:mb-0">
              {missing.length ? `Still needed: ${missing.join(', ')}.` : 'Ready for review. You choose when to send there.'}
            </p>
            <div className="flex flex-wrap items-center gap-2 md:gap-3">
              {notice === 'saved' && (
                <span role="status" className="basis-full text-sm text-fg-muted md:basis-auto">
                  Draft saved
                </span>
              )}
              {notice === 'failed' && <span className="basis-full text-sm text-danger md:basis-auto">That did not work. Try again.</span>}
              {notice === 'gone' && <span className="basis-full text-sm text-danger md:basis-auto">This message is no longer a draft.</span>}
              <button type="button" className={classNames(btn.secondary, 'flex-1 md:flex-none')} disabled={!draft.title.trim() || badLink || busy} onClick={() => save(false)}>
                Save draft
              </button>
              <button type="button" className={classNames(btn.primary, 'flex-1 md:flex-none')} disabled={missing.length > 0 || busy} onClick={() => save(true)}>
                Review &amp; send
                <span aria-hidden>→</span>
              </button>
            </div>
          </ActionBar>
        </Card>

        <div className="space-y-4 md:space-y-6">
          <Card title="Preview" aside={<span className="text-xs text-fg-faint">With sample values</span>}>
            <PreviewPanel channels={draft.channels} title={draft.title} body={draft.body} linkLabel={linkButtonLabel(draft)} />
          </Card>
          <Card title="Audience">
            <AudienceList segments={draft.segment_ids} channels={draft.channels} />
          </Card>
        </div>
      </div>
    </div>
  );
};

/** Loads a stored draft before editing it; a message that is no longer a draft cannot be edited. */
const EditDraft = ({ id }: { id: string }) => {
  const { data, error } = useApi<MessageDetail>(`/messages/${encodeURIComponent(id)}`);
  if (error === 'Not found.') return <NotFound what="message" back="/messaging/messages" label="Back to messages" />;
  if (error) return <StateMessage title="The message could not be loaded" tone="error">{error}</StateMessage>;
  if (!data) return <div className="h-64 animate-pulse rounded-card bg-surface shadow-e1" />;
  if (data.status !== 'draft') {
    return (
      <StateMessage title="Only drafts can be edited">
        This message is {data.status}.{' '}
        <Link to={`/messaging/messages/${data.id}`} className={btn.link}>
          Open it
        </Link>
      </StateMessage>
    );
  }
  return <Composer key={data.id} id={data.id} stored={data} />;
};

/** New message, or edit a draft (OUT-1…6, SEG-3): text with variables, channels, segments, and a live preview and audience. */
const ComposePage = () => {
  const { id } = useParams();
  const location = useLocation();
  return id ? <EditDraft key={id} id={id} /> : <Composer key={`new:${location.key}`} />;
};

export default ComposePage;
